import React, { useRef } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { Button } from '../../../../components/core';
import { usePreviewAgenticVoiceMutation } from '../../../../store/modules/merchant/api';
import { toast } from '../../../../util/toast';
import { type AgentVoice, defaultVoiceSample } from '../../agentCatalog';

const agentActionButtonSx = {
  height: 48,
  minWidth: 125,
  px: 2,
  flexShrink: 0,
  whiteSpace: 'nowrap',
} as const;

const getSaveErrorMessage = (err: unknown, fallback: string): string => {
  const errors = (err as { data?: { errors?: string[] } })?.data?.errors;
  return Array.isArray(errors) && errors.length > 0 ? errors.join(' ') : fallback;
};

type Props = {
  merchantId?: string;
  agentDisplayName: string;
  voiceProfileId: string;
  defaultVoiceProfileId: string;
  voiceOptions: readonly AgentVoice[];
  onVoiceProfileIdChange: (profileId: string) => void;
  disabled?: boolean;
  loadingVoices?: boolean;
  voiceSelectTestId?: string;
  playVoiceTestId?: string;
};

export const AgentVoiceSelector = ({
  merchantId,
  agentDisplayName,
  voiceProfileId,
  defaultVoiceProfileId,
  voiceOptions,
  onVoiceProfileIdChange,
  disabled = false,
  loadingVoices = false,
  voiceSelectTestId = 'ella-agent-voice',
  playVoiceTestId = 'ella-play-voice',
}: Props): JSX.Element => {
  const [previewAgenticVoice, { isLoading: isPreviewingVoice }] = usePreviewAgenticVoiceMutation();
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const voiceSample = defaultVoiceSample(agentDisplayName);
  const effectiveVoiceProfileId = voiceProfileId.trim() || defaultVoiceProfileId;
  const isDefaultVoiceSelected =
    Boolean(defaultVoiceProfileId) && effectiveVoiceProfileId === defaultVoiceProfileId;

  const voiceOptionLabel = (option: AgentVoice): string =>
    option.profileId === defaultVoiceProfileId && defaultVoiceProfileId
      ? `${option.label} (Default)`
      : option.label;

  const selectedVoiceLabel = voiceOptions.find(
    (option) => option.profileId === effectiveVoiceProfileId,
  )?.label;

  React.useEffect(
    () => () => {
      previewAudioRef.current?.pause();
      if (previewAudioRef.current?.src) {
        URL.revokeObjectURL(previewAudioRef.current.src);
      }
    },
    [],
  );

  const handlePlayVoice = async () => {
    if (!merchantId || !effectiveVoiceProfileId || isPreviewingVoice) {
      return;
    }

    try {
      const audioBlob = await previewAgenticVoice({
        merchantId,
        agentName: 'Ella',
        body: {
          voiceProfileId: effectiveVoiceProfileId,
          text: voiceSample,
        },
      }).unwrap();

      previewAudioRef.current?.pause();
      if (previewAudioRef.current?.src) {
        URL.revokeObjectURL(previewAudioRef.current.src);
      }

      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      previewAudioRef.current = audio;
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
      };
      await audio.play();
    } catch (err) {
      toast.error(getSaveErrorMessage(err, 'Failed to preview voice'));
    }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Voice</Typography>
      <Typography variant="small" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Select a voice and preview it.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <FormControl fullWidth disabled={disabled || loadingVoices}>
          <InputLabel id="ella-agent-voice-label" shrink>
            Voice
          </InputLabel>
          <Select
            labelId="ella-agent-voice-label"
            label="Voice"
            notched
            displayEmpty
            value={effectiveVoiceProfileId}
            renderValue={(value) => {
              const option = voiceOptions.find((item) => item.profileId === value);
              if (!option) {
                return loadingVoices ? 'Loading voices...' : 'Select a voice';
              }
              return voiceOptionLabel(option);
            }}
            onChange={(event: SelectChangeEvent) => onVoiceProfileIdChange(event.target.value)}
            inputProps={{ 'aria-label': 'Voice' }}
            data-testid={voiceSelectTestId}
          >
            {loadingVoices && voiceOptions.length === 0 ? (
              <MenuItem disabled value="">
                Loading voices...
              </MenuItem>
            ) : (
              voiceOptions.map((option) => (
                <MenuItem key={option.profileId} value={option.profileId}>
                  {voiceOptionLabel(option)}
                </MenuItem>
              ))
            )}
          </Select>
          {isDefaultVoiceSelected && selectedVoiceLabel ? (
            <Typography
              variant="small"
              color="text.secondary"
              sx={{ mt: 1, display: 'block' }}
              data-testid="ella-agent-voice-default-hint"
            >
              {selectedVoiceLabel} is selected by default. You can choose a different voice anytime.
            </Typography>
          ) : null}
        </FormControl>
        <Box sx={{ height: 56, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Button
            theme="secondary"
            text="Play voice"
            startIcon={<PlayArrowIcon />}
            dataTestId={playVoiceTestId}
            disabled={
              disabled ||
              loadingVoices ||
              !merchantId ||
              !effectiveVoiceProfileId ||
              isPreviewingVoice
            }
            loading={isPreviewingVoice}
            onClick={() => {
              void handlePlayVoice();
            }}
            sx={agentActionButtonSx}
          />
        </Box>
      </Box>
    </Box>
  );
};
